/**
 * Cart recommendations ("Pairs well with").
 *
 * Rule-based cross-sell for the cart drawer and cart page. Reads the cart (/cart.js),
 * applies the ladders in snippets/cart-recommendations-config.liquid, checks live stock
 * and price via /products/<handle>.js, picks the size- and colour-matched variant and
 * hands the theme's existing PairWithProduct renderer a list of "handle_variantId" strings.
 *
 * Layers: L1 complete the set (same family), L2 coordinate (same family), L3 universal
 * add-on (slot 3), L4 best-seller fallback. Evidence: docs/cart-recommendation-logic.md.
 *
 * Whole-cart rules: every cart item contributes suggestions, led by the hero (the highest-ranked
 * item in CONFIG.heroRank, newest first on a tie). Size comes from the bed (pillow pieces from a
 * pillow item already in the cart). Colour goes by layer: pillow pieces match the cart's pillow
 * items, bed pieces match the bed item. Accents (throws, cushions) contrast a one-colour cart
 * (CONFIG.accentContrast) and alternate through the colours of a mixed cart.
 */
(function () {
  const CONFIG = window.cartRecommendationsConfig;
  if (!CONFIG) return;

  const cache = {};
  const products = {};
  const meta = {};

  // A product request slower than this counts as unavailable, so one stalled request can't hold up the row
  const FETCH_TIMEOUT = 3000;
  // compute() stops looking for more candidates after this and returns what it has
  const COMPUTE_BUDGET = 4000;

  const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';

  function fetchProduct(handle) {
    if (!cache[handle]) {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT) : null;
      cache[handle] = fetch(`${root}products/${handle}.js`, controller ? { signal: controller.signal } : undefined)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
        .then((product) => {
          clearTimeout(timer);
          if (product) {
            products[handle] = product;
          } else {
            // Don't remember a failure: the next cart update gets a fresh try
            delete cache[handle];
          }
          return product;
        });
    }
    return cache[handle];
  }

  /**
   * Product JSON the engine already fetched, as a copy (the theme's formatPrices rewrites prices in place),
   * so the card renderer doesn't request it again. Null when the engine doesn't have it.
   */
  function getProduct(handle) {
    return products[handle] ? JSON.parse(JSON.stringify(products[handle])) : null;
  }

  function familiesOf(handle) {
    const p = CONFIG.products[handle];
    return p ? [].concat(p.family) : [];
  }

  function isGiftLine(item) {
    if (!item.final_line_price || item.price === 0) return true;
    const props = item.properties || {};
    return Object.keys(props).some((k) => /gift|gwp/i.test(k));
  }

  function lineInfo(item) {
    const entry = CONFIG.products[item.handle] || {};
    let size = null;
    let colour = null;
    (item.options_with_values || []).forEach((o) => {
      const name = norm(o.name);
      const value = norm(o.value);
      if (/size/.test(name) || /^uk (single|double|king|super king)$|standard/.test(value)) size = value;
      if (/colou?r/.test(name)) colour = value;
    });
    // Only guess the colour from the variant title when the line has no option data at all, and never
    // from a size or Shopify's "Default Title" (either would make a one-colour cart look mixed).
    if (!colour && !(item.options_with_values || []).length && item.variant_title) {
      const guess = norm(item.variant_title.split('/')[0]);
      if (guess && guess !== 'default title' && !/^uk |standard|\d/.test(guess)) colour = guess;
    }
    return {
      handle: item.handle,
      role: entry.role || null,
      families: familiesOf(item.handle),
      price: item.price,
      size,
      colour,
      gift: isGiftLine(item),
    };
  }

  function sizeFor(anchor, companionRole) {
    if (!anchor || !anchor.size) return null;
    const group = CONFIG.bedRoles.includes(companionRole) ? 'bed' : CONFIG.pillowRoles.includes(companionRole) ? 'pillow' : null;
    if (!group) return null;
    return CONFIG.sizeMap[group][anchor.size] || null;
  }

  const isBedRole = (role) => CONFIG.bedRoles.includes(role);
  const isPillowRole = (role) => CONFIG.pillowRoles.includes(role) || role === 'shams-euro';
  const isAccentRole = (role) => (CONFIG.accentRoles || []).includes(role);
  const heroRank = (role) => ((CONFIG.heroRank || {})[role] === undefined ? 99 : CONFIG.heroRank[role]);

  /**
   * The size and colours a candidate should match, from the whole cart.
   * Returns { size, colours: [{ colour, weight }] } with the preferred colour first.
   */
  function targetFor(role, candidate, ctx, state) {
    const { hero, bedLine, pillowLine, pillowSizeLine, palette } = ctx;
    const heroColour = hero && hero.colour;
    const contrastFor = (c) => (c && (CONFIG.accentContrast || {})[c]) || [];
    const colours = [];
    const add = (colour, weight) => {
      if (colour && !colours.some((t) => t.colour === colour)) colours.push({ colour, weight });
    };
    let size = null;

    if (isAccentRole(role)) {
      // One-colour cart: throws and cushions contrast it (all-white bedding gets a Biscotti throw,
      // not another white piece). Mixed cart: the shopper already chose the contrast, so accents take
      // turns through the cart's colours. Colours another accent already uses are avoided (pickVariant).
      if (palette.length === 1) {
        contrastFor(palette[0]).forEach((c, i) => add(c, Math.max(1 - i * 0.1, 0.5)));
      } else if (palette.length) {
        const start = state.accentIndex % palette.length;
        palette.slice(start).concat(palette.slice(0, start)).forEach((c, i) => add(c, i === 0 ? 1 : 0.6));
      }
      palette.forEach((c) => add(c, 0.3));
      return { size, colours, avoid: state.accentColours };
    }

    let colour;
    if (isPillowRole(role)) {
      colour = (pillowLine && pillowLine.colour) || heroColour;
      size = pillowSizeLine && pillowSizeLine.size ? pillowSizeLine.size : sizeFor(bedLine || hero, role);
    } else if (isBedRole(role)) {
      colour = (bedLine && bedLine.colour) || heroColour;
      size = sizeFor(bedLine || hero, role);
    } else {
      const anchor = candidate.anchor || hero;
      colour = anchor && anchor.colour;
      size = sizeFor(anchor, role);
    }

    // Fallback chain when the product lacks the preferred colour: the cart's other colours, then a
    // styling-approved contrast for the hero, before whatever the product happens to list first.
    add(colour, 1);
    palette.forEach((c) => add(c, 0.4));
    contrastFor(heroColour).forEach((c) => add(c, 0.25));
    return { size, colours, avoid: [] };
  }

  /**
   * How well a companion colour matches the anchor colour, 0-10.
   * Exact name 10, then CONFIG.colourAliases in listed order (UK names differ across ranges:
   * the comforter's "White" is "Clear White" on the bamboo sheets and shams), then any shared word.
   */
  function colourScore(candidate, wanted) {
    if (!candidate || !wanted) return 0;
    if (candidate === wanted) return 10;
    const aliases = (CONFIG.colourAliases || {})[wanted] || [];
    const aliasIdx = aliases.indexOf(candidate);
    if (aliasIdx > -1) return Math.max(9 - aliasIdx, 5);
    const words = wanted.split(' ');
    if (candidate.split(' ').some((w) => words.includes(w))) return 4;
    return 0;
  }

  function variantColour(product, variant) {
    const options = (product.options || []).map((o) => norm(typeof o === 'string' ? o : o.name));
    const idx = options.findIndex((o) => /colou?r/.test(o));
    return idx > -1 ? norm([variant.option1, variant.option2, variant.option3][idx]) : null;
  }

  function pickVariant(product, target) {
    const options = (product.options || []).map((o) => norm(typeof o === 'string' ? o : o.name));
    const sizeIdx = options.findIndex((o) => /size/.test(o));
    const colourIdx = options.findIndex((o) => /colou?r/.test(o));
    const wantSize = target.size;
    let best = null;
    let bestScore = -1;
    (product.variants || []).forEach((v) => {
      if (!v.available) return;
      let score = 0;
      const opts = [v.option1, v.option2, v.option3].map(norm);
      // Size outweighs colour: a wrong-size sham is unusable, a near-colour one is not.
      if (wantSize && sizeIdx > -1) {
        if (opts[sizeIdx] === wantSize) score += 40;
        else if (opts[sizeIdx] && opts[sizeIdx].replace('uk ', '') === wantSize.replace('uk ', '')) score += 30;
      }
      if (colourIdx > -1) {
        let colourPart = Math.max(0, ...target.colours.map((t) => colourScore(opts[colourIdx], t.colour) * t.weight));
        // Two accents in the same colour look like a mistake: strongly prefer a colour not yet shown
        if ((target.avoid || []).some((c) => colourScore(opts[colourIdx], c) >= 5)) colourPart *= 0.2;
        score += colourPart;
      }
      if (score > bestScore) {
        bestScore = score;
        best = v;
      }
    });
    return best;
  }

  function isOutlet(product) {
    const tags = (product.tags || []).map(norm);
    if (CONFIG.outletTags.some((t) => tags.includes(t))) return true;
    return norm(product.title).includes(CONFIG.outletTitle);
  }

  function handlesFor(role, families) {
    const out = [];
    Object.keys(CONFIG.products).forEach((h) => {
      const p = CONFIG.products[h];
      if (p.role !== role) return;
      if (families && !families.some((f) => [].concat(p.family).includes(f))) return;
      out.push(h);
    });
    const prio = (h) => (CONFIG.products[h].priority === undefined ? 50 : CONFIG.products[h].priority);
    return out.sort((a, b) => prio(a) - prio(b));
  }

  /**
   * Build the ordered candidate list for a cart.
   * Each candidate: { handle, layer, anchor, priceExempt, slotMin }
   */
  function buildCandidates(lines) {
    // Hero first: highest-ranked role, then newest (cart.js lists the newest line first).
    const anchors = lines
      .map((l, idx) => ({ l, idx }))
      .filter(({ l }) => !l.gift && l.role)
      .sort((a, b) => heroRank(a.l.role) - heroRank(b.l.role) || a.idx - b.idx)
      .map(({ l }) => l);
    const L1 = [];
    const L2 = [];
    const seen = new Set();
    const push = (list, c) => {
      const key = c.handle;
      if (seen.has(key)) return;
      seen.add(key);
      list.push(c);
    };

    anchors.forEach((anchor, idx) => {
      const rules = CONFIG.ladders[anchor.role] || {};
      (rules.complete || []).forEach((role) => {
        handlesFor(role, anchor.families).forEach((handle) => {
          push(L1, {
            handle,
            layer: 'L1',
            anchor,
            priceExempt: Boolean(rules.outfit || rules.reverse),
            slotMin: rules.reverse ? 2 : 1,
            anchorIndex: idx,
          });
        });
      });
      (rules.coordinate || []).forEach((role) => {
        handlesFor(role, rules.coordinateAnyFamily ? null : anchor.families).forEach((handle) => {
          push(L2, { handle, layer: 'L2', anchor, priceExempt: false, slotMin: 1, anchorIndex: idx });
        });
      });
    });

    const primary = anchors[0] || null;
    const L3 = CONFIG.universal.map((handle) => ({ handle, layer: 'L3', anchor: primary, priceExempt: false, slotMin: 3 }));
    const L4 = CONFIG.fallback.map((handle) => ({ handle, layer: 'L4', anchor: null, priceExempt: false, slotMin: 1 }));
    return { anchors, L1, L2, L3, L4 };
  }

  /**
   * Resolve a candidate against live product data. Returns the chosen variant or null.
   */
  function resolve(candidate, state, ctx) {
    const entry = CONFIG.products[candidate.handle];
    if (!entry) return Promise.resolve(null);
    if (state.handlesInCart.has(candidate.handle)) return Promise.resolve(null);
    if (state.handlesShown.has(candidate.handle)) return Promise.resolve(null);
    if (state.blockedRoles.has(entry.role)) return Promise.resolve(null);
    const dup = CONFIG.allowDuplicateRoles.includes(entry.role);
    const shown = state.rolesShown[entry.role] || 0;
    if (!dup && (state.rolesInCart.has(entry.role) || shown >= 1)) return Promise.resolve(null);
    if (dup && shown >= 2) return Promise.resolve(null);
    if (state.fetches >= CONFIG.maxFetches) return Promise.resolve(null);
    // Past the time budget only products already loaded are used
    const remaining = state.deadline - Date.now();
    if (remaining <= 0 && !products[candidate.handle]) return Promise.resolve(null);
    state.fetches += 1;

    const timeout = new Promise((done) => setTimeout(() => done(null), Math.max(remaining, 0)));
    return Promise.race([fetchProduct(candidate.handle), timeout]).then((product) => {
      if (!product || !product.available || isOutlet(product)) return null;
      const anchor = candidate.anchor;
      const variant = pickVariant(product, targetFor(entry.role, candidate, ctx, state));
      if (!variant) return null;
      const cap = candidate.layer === 'L3' ? state.maxLinePrice : anchor ? anchor.price : state.maxLinePrice;
      if (!candidate.priceExempt && cap && variant.price > cap) return null;
      return { candidate, product, variant };
    });
  }

  /**
   * Start the product requests compute() is likely to need, all at once, so its one-at-a-time walk
   * through the ladders reads from the cache instead of waiting on each request in turn. Takes
   * candidates from each layer in turn, skipping ones the static cart rules already rule out, up to
   * CONFIG.maxFetches. The selection itself is unchanged.
   */
  function prefetch(groups, state) {
    const queues = groups.map((g) => g.slice());
    const started = new Set();
    let more = true;
    while (more && started.size < CONFIG.maxFetches) {
      more = false;
      for (const queue of queues) {
        while (queue.length) {
          const handle = queue.shift().handle;
          const entry = CONFIG.products[handle];
          if (started.has(handle) || !entry || state.handlesInCart.has(handle) || state.blockedRoles.has(entry.role)) continue;
          if (!CONFIG.allowDuplicateRoles.includes(entry.role) && state.rolesInCart.has(entry.role)) continue;
          started.add(handle);
          fetchProduct(handle);
          more = true;
          break;
        }
        if (started.size >= CONFIG.maxFetches) break;
      }
    }
  }

  async function fillSlot(slotNumber, groups, state, ctx) {
    for (const group of groups) {
      for (const c of group) {
        if (c.slotMin > slotNumber) continue;
        if (c.layer === 'L1' && slotNumber === 1 && c.slotMin === 2) continue;
        const hit = await resolve(c, state, ctx);
        if (hit) {
          const role = CONFIG.products[hit.candidate.handle].role;
          state.handlesShown.add(hit.candidate.handle);
          state.rolesShown[role] = (state.rolesShown[role] || 0) + 1;
          if (isAccentRole(role)) {
            state.accentIndex += 1;
            const shownColour = variantColour(hit.product, hit.variant);
            if (shownColour) state.accentColours.push(shownColour);
          }
          // A shown role blocks its conflicts too (no fitted sheet next to a fitted sheet set)
          ((CONFIG.conflicts || {})[role] || []).forEach((r) => state.blockedRoles.add(r));
          return hit;
        }
      }
    }
    return null;
  }

  /**
   * compute(cart, overrides) -> Promise<string[]> of "handle_variantId" for PairWithProduct.
   * overrides: handles from the legacy _upsell_<handle> tags, placed first.
   */
  async function compute(cart, overrides) {
    const lines = (cart && cart.items ? cart.items : []).map(lineInfo);
    if (!lines.length) return [];
    const state = {
      handlesInCart: new Set(lines.map((l) => l.handle)),
      rolesInCart: new Set(lines.filter((l) => !l.gift && l.role).map((l) => l.role)),
      handlesShown: new Set(),
      rolesShown: {},
      blockedRoles: new Set(),
      maxLinePrice: Math.max.apply(null, lines.filter((l) => !l.gift).map((l) => l.price).concat([0])),
      fetches: 0,
      deadline: Date.now() + COMPUTE_BUDGET,
      accentIndex: 0,
      accentColours: [],
    };
    state.rolesInCart.forEach((role) => {
      ((CONFIG.conflicts || {})[role] || []).forEach((r) => state.blockedRoles.add(r));
    });
    const { anchors, L1, L2, L3, L4 } = buildCandidates(lines);
    prefetch([L1, L2, L3, L4], state);
    const results = [];

    // Whole-cart context: hero, the bed item, the newest pillow item, and the cart's colours (hero first)
    const hero = anchors[0] || null;
    // Near-identical names (White, Clear White) count as one colour, so a White comforter with
    // Clear White shams is a one-colour cart.
    const palette = [];
    anchors.forEach((a) => {
      if (a.colour && !palette.some((c) => colourScore(a.colour, c) >= 5 || colourScore(c, a.colour) >= 5)) palette.push(a.colour);
    });
    const ctx = {
      hero,
      bedLine: anchors.find((a) => isBedRole(a.role)) || null,
      // Newest pillow item for colour (euro shams included); newest sham or pillowcase for size, since a
      // euro sham's 26" x 26" must not be copied onto shams and pillowcases
      pillowLine: lines.find((l) => !l.gift && isPillowRole(l.role)) || null,
      pillowSizeLine: lines.find((l) => !l.gift && CONFIG.pillowRoles.includes(l.role)) || null,
      palette,
    };

    // Legacy tag overrides first (handle or handle_variantId), unfiltered beyond cart membership.
    (overrides || []).forEach((h) => {
      const handle = String(h).split('_')[0];
      if (!state.handlesInCart.has(handle) && !state.handlesShown.has(handle)) {
        state.handlesShown.add(handle);
        results.push({ key: h, layer: 'override', anchor: anchors[0] ? anchors[0].handle : '' });
      }
    });

    const primaryHasL2 = L2.some((c) => c.anchorIndex === 0);
    const plans = [
      [L1, L2, L4],
      primaryHasL2 ? [L2, L1, L4] : [L1, L2, L4],
      [L3, L1, L2, L4],
      [L2, L1, L3, L4],
    ];
    for (let slot = results.length + 1; slot <= CONFIG.slots; slot += 1) {
      const hit = await fillSlot(slot, plans[Math.min(slot, plans.length) - 1], state, ctx);
      if (!hit) continue;
      const key = `${hit.candidate.handle}_${hit.variant.id}`;
      meta[hit.variant.id] = { layer: hit.candidate.layer, anchor: hit.candidate.anchor ? hit.candidate.anchor.handle : '' };
      results.push({ key, layer: hit.candidate.layer });
    }
    return results.map((r) => r.key);
  }

  window.cartRecommendations = { compute, meta, getProduct };

  // theme.dev.js is deferred in <head>, so CartDrawer is built before this file runs and its
  // first render falls back to the tag list. Re-run it now the engine is available.
  if (window.cart && typeof window.cart.refreshRecommendations === 'function') {
    window.cart.refreshRecommendations(null, window.pairWithProducts);
  }
})();
